/**
 * JWT 토큰 디코딩 유틸리티
 *
 * atob()는 UTF-8 인코딩된 문자열을 제대로 처리하지 못하므로,
 * 한글 등 멀티바이트 문자가 포함된 JWT 토큰을 올바르게 디코딩하기 위한 유틸리티 함수
 */

/**
 * Base64 URL-safe 디코딩 후 UTF-8 문자열로 변환
 * atob()는 UTF-8을 제대로 처리하지 못하므로 TextDecoder를 사용
 *
 * @param token - JWT 토큰 문자열 (전체 토큰 또는 payload 부분)
 * @returns 디코딩된 페이로드 객체
 * @throws {Error} 토큰 디코딩에 실패한 경우
 */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  // 입력 검증
  if (!token || typeof token !== "string") {
    throw new Error(
      "토큰이 유효하지 않습니다: 토큰이 없거나 문자열이 아닙니다",
    );
  }

  if (token.trim().length === 0) {
    throw new Error("토큰이 유효하지 않습니다: 빈 문자열입니다");
  }

  try {
    // 토큰이 전체 JWT인 경우 payload 부분만 추출
    const tokenParts = token.split(".");
    const base64Url = tokenParts.length === 3 ? tokenParts[1] : tokenParts[0];

    if (!base64Url || base64Url.length === 0) {
      throw new Error("토큰 형식이 올바르지 않습니다: payload 부분이 없습니다");
    }

    // Base64 URL-safe를 일반 Base64로 변환
    // 패딩 추가 (Base64는 4의 배수여야 함)
    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padding = base64.length % 4;
    if (padding) {
      base64 += "=".repeat(4 - padding);
    }

    // Base64 디코딩
    let binaryString: string;
    try {
      binaryString = atob(base64);
    } catch (atobError) {
      console.error("[JWT Utils] Base64 디코딩 실패:", {
        error: atobError,
        base64Length: base64.length,
        base64Prefix: base64.substring(0, 20),
      });
      throw new Error(
        "Base64 디코딩에 실패했습니다. 토큰 형식이 올바르지 않을 수 있습니다",
      );
    }

    // UTF-8 바이트 배열로 변환
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // TextDecoder를 사용하여 UTF-8 디코딩
    const decoder = new TextDecoder("utf-8");
    let jsonPayload: string;
    try {
      jsonPayload = decoder.decode(bytes);
    } catch (decodeError) {
      console.error("[JWT Utils] UTF-8 디코딩 실패:", decodeError);
      throw new Error("UTF-8 디코딩에 실패했습니다");
    }

    // JSON 파싱
    try {
      return JSON.parse(jsonPayload);
    } catch (parseError) {
      console.error("[JWT Utils] JSON 파싱 실패:", {
        error: parseError,
        jsonPayloadLength: jsonPayload.length,
        jsonPayloadPrefix: jsonPayload.substring(0, 100),
      });
      throw new Error(
        "JSON 파싱에 실패했습니다. 토큰 내용이 올바르지 않을 수 있습니다",
      );
    }
  } catch (error) {
    // 이미 구체적인 에러 메시지를 가진 경우 그대로 던짐
    if (error instanceof Error && error.message.includes("토큰")) {
      throw error;
    }

    // 기타 에러는 로깅 후 일반 메시지로 변환
    console.error("[JWT Utils] 디코딩 실패:", {
      error,
      tokenLength: token.length,
      tokenPrefix: token.substring(0, 50),
      hasDots: token.includes("."),
    });
    throw new Error(
      `JWT 토큰 디코딩에 실패했습니다: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}
